import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const CompassAnimation = ({ onComplete }) => {
  // 为星空创建随机星星
  const [stars, setStars] = useState([]);
  
  useEffect(() => {
    // 生成随机星星
    const generateStars = () => {
      const starCount = 100; // 增加星星数量
      const newStars = [];
      
      for (let i = 0; i < starCount; i++) {
        newStars.push({
          id: i,
          x: Math.random() * 100, // 横向位置百分比
          y: Math.random() * 100, // 纵向位置百分比
          size: Math.random() * 3 + 1, // 1-4px大小
          opacity: Math.random() * 0.7 + 0.3, // 0.3-1不等的透明度
          blinkDuration: Math.random() * 3 + 2, // 2-5秒的闪烁周期
          delay: Math.random() * 2 // 随机延迟
        });
      }
      
      setStars(newStars);
    };
    
    generateStars();
  }, []);
  // 在组件挂载后启动计时器，几秒后触发完成回调
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 3000); // 4秒后触发回调

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center w-screen h-screen fixed top-0 left-0 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      style={{
        background: 'linear-gradient(to bottom, #0a0a2a, #1a1a3a, #0a0a2a)',
        overflow: 'hidden'
      }}
    >
      {/* 星空背景 */}
      <div className="absolute inset-0 overflow-hidden">
        {stars.map((star) => (
          <motion.div
            key={star.id}
            className="absolute rounded-full bg-white"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
            }}
            animate={{
              opacity: [star.opacity, star.opacity * 1.5, star.opacity],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: star.blinkDuration,
              delay: star.delay,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>
      
      {/* 神秘光晕 */}
      <motion.div
        className="absolute rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(183, 132, 255, 0.2) 0%, rgba(183, 132, 255, 0) 70%)',
          width: '80vmin',
          height: '80vmin'
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.4, 0.6, 0.4]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
      {/* 罗盘外环 */}
      <motion.div
        className="relative rounded-full flex items-center justify-center z-10"
        style={{
          width: '50vmin',
          height: '50vmin',
          background: 'linear-gradient(135deg, rgba(247, 132, 255, 0.2), rgba(33, 33, 66, 0.6))',
          boxShadow: '0 0 15px rgba(247, 132, 255, 0.5), inset 0 0 8px rgba(247, 132, 255, 0.3)',
          border: '2px solid rgba(247, 132, 255, 0.6)'
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      >
        {/* 罗盘内环 */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: '40vmin',
            height: '40vmin',
            background: 'radial-gradient(circle, rgba(33, 33, 66, 0.6) 0%, rgba(33, 33, 66, 0.8) 90%)',
            boxShadow: 'inset 0 0 10px rgba(183, 132, 255, 0.4)',
            border: '1px solid rgba(183, 132, 255, 0.7)'
          }}
          animate={{ rotate: -180 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          {/* 罗盘中心标记 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="rounded-full"
              style={{
                width: '8vmin',
                height: '8vmin',
                background: 'radial-gradient(circle, rgba(247, 132, 255, 0.9) 0%, rgba(183, 132, 255, 0.5) 70%)',
                boxShadow: '0 0 10px rgba(247, 132, 255, 0.8)'
              }}
              animate={{ scale: [1, 1.2, 1], boxShadow: ['0 0 10px rgba(247, 132, 255, 0.8)', '0 0 20px rgba(247, 132, 255, 0.9)', '0 0 10px rgba(247, 132, 255, 0.8)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            
            {/* 中心符文 */}
            <motion.div 
              className="absolute text-white text-lg font-bold"
              style={{
                opacity: 0.9,
                textShadow: '0 0 5px rgba(255, 255, 255, 0.8)'
              }}
              animate={{ rotate: 360, opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            >
              ✧
            </motion.div>
          </div>
          
          {/* 罗盘指针 */}
          <motion.div
            className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '0.5vmin',
              height: '10vmin',
              background: 'linear-gradient(to top, rgba(247, 132, 255, 0.9), rgba(183, 132, 255, 0.4), transparent)',
              boxShadow: '0 0 8px rgba(247, 132, 255, 0.6)'
            }}
            animate={{ rotate: 360, height: ['10vmin', '12vmin', '10vmin'] }}
            transition={{ 
              rotate: { duration: 4, repeat: Infinity, ease: "linear" },
              height: { duration: 2, repeat: Infinity, repeatType: "reverse" }
            }}
          />
          
          {/* 第二罗盘指针 - 逆向旋转 */}
          <motion.div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2"
            style={{
              width: '0.5vmin',
              height: '8vmin',
              background: 'linear-gradient(to bottom, rgba(183, 132, 255, 0.9), rgba(247, 132, 255, 0.4), transparent)',
              boxShadow: '0 0 8px rgba(183, 132, 255, 0.6)'
            }}
            animate={{ rotate: -360, height: ['8vmin', '10vmin', '8vmin'] }}
            transition={{ 
              rotate: { duration: 6, repeat: Infinity, ease: "linear" },
              height: { duration: 3, repeat: Infinity, repeatType: "reverse", delay: 1 }
            }}
          />
          
          {/* 八卦位置标记 */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((degree, index) => {
            // 符号数组 - 八卦符号或神秘符文
            const symbols = ['☰', '☱', '☲', '☳', '☴', '☵', '☶', '☷'];
            return (
              <div
                key={index}
                className="absolute w-full h-full flex items-center justify-center"
                style={{ transform: `rotate(${degree}deg)` }}
              >
                {/* 外圈光点 */}
                <motion.div
                  className="absolute top-0 rounded-full"
                  style={{
                    width: '1.5vmin',
                    height: '1.5vmin',
                    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(183, 132, 255, 0.7) 70%)',
                    boxShadow: '0 0 5px rgba(255, 255, 255, 0.8)'
                  }}
                  animate={{ 
                    opacity: [0.7, 1, 0.7],
                    boxShadow: ['0 0 3px rgba(255, 255, 255, 0.6)', '0 0 8px rgba(255, 255, 255, 0.9)', '0 0 3px rgba(255, 255, 255, 0.6)']
                  }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.3 }}
                />
                
                {/* 内圈符号 */}
                <div 
                  className="absolute"
                  style={{
                    top: 'calc(50% - 30px)',
                    left: '50%',
                    transform: 'translateX(-50%) translateY(-50%) rotate(-' + degree + 'deg)', // 保持符号正向
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 'calc(1vmin + 8px)',
                    textShadow: '0 0 5px rgba(183, 132, 255, 0.8)'
                  }}
                >
                  <motion.span
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
                  >
                    {symbols[index]}
                  </motion.span>
                </div>
              </div>
            );
          })}
        </motion.div>
        
        {/* 罗盘符文 - 光芒效果 */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-full h-full"
            style={{ transform: `rotate(${i * 30}deg)` }}
          >
            <motion.div
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{

                borderRadius: '4px',
                background: 'linear-gradient(to top, rgba(247, 132, 255, 0.8), rgba(255, 255, 255, 0.9))',
                boxShadow: '0 0 4px rgba(247, 132, 255, 0.7)'
              }}
              animate={{ 
                height: ['8px', '12px', '8px'],
                opacity: [0.7, 1, 0.7],
                boxShadow: ['0 0 4px rgba(247, 132, 255, 0.7)', '0 0 8px rgba(247, 132, 255, 0.9)', '0 0 4px rgba(247, 132, 255, 0.7)'],
              }}
              transition={{ 
                duration: 2, 
                delay: i * 0.1, 
                repeat: Infinity,
                repeatType: "reverse" 
              }}
            />
          </motion.div>
        ))}
      </motion.div>
      
      {/* 说明文字 */}
      <motion.p
        className="mt-8 text-center text-white px-6"
        style={{ 
          textShadow: '0 0 8px rgba(247, 132, 255, 0.6)',
          maxWidth: '80vw',
          fontSize: 'calc(1vmin + 10px)'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        Let AI reveal your financial destiny through ancient birth-based wisdom — personalized just for you and powered by DeepSeek
      </motion.p>
      
      {/* 加载指示器 */}
      <motion.div
        className="mt-8 flex items-center"
        style={{
          gap: 'calc(1vmin + 4px)'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="rounded-full"
            style={{
              width: 'calc(0.8vmin + 2px)',
              height: 'calc(0.8vmin + 2px)',
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(247, 132, 255, 0.8))',
              boxShadow: '0 0 5px rgba(247, 132, 255, 0.7)'
            }}
            animate={{ 
              scale: [1, 1.5, 1], 
              opacity: [0.3, 1, 0.3],
              boxShadow: ['0 0 5px rgba(247, 132, 255, 0.7)', '0 0 10px rgba(247, 132, 255, 1)', '0 0 5px rgba(247, 132, 255, 0.7)'] 
            }}
            transition={{ 
              duration: 1.2, 
              repeat: Infinity, 
              delay: i * 0.2,
              repeatType: "loop" 
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

export default CompassAnimation;
